import {
  AppleMac,
  Car,
  Check,
  ChatBubbleWarning,
  Infinite,
  Internet,
  MailIn,
  Page,
  ForwardMessage,
  SoundHigh,
  Puzzle,
  SmartphoneDevice,
  Windows,
  Wristwatch,
  Xmark,
} from 'iconoir-react';
import type { ReactNode } from 'react';
import Waveform from '@listening/assets/astro/icons/new-design-25/compare/waveform.svg?react';
import { cn } from '@listening/utils/react-helpers';
import * as m from '@/paraglide/messages';

function FloatingCard({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'bg-white text-(--v2-text-primary) shadow-[0_1px_2px_rgb(0_0_0/0.05),0_8px_24px_-12px_rgb(0_0_0/0.12)] ring-1 ring-black/[0.06]',
        className,
      )}
    >
      {children}
    </div>
  );
}

function Stage({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'relative flex min-h-32 flex-1 overflow-hidden rounded-[18px] bg-white bg-[radial-gradient(rgb(21_22_26/0.07)_1px,transparent_1px)] bg-size-[16px_16px] ring-1 ring-black/[0.05] sm:min-h-40',
        className,
      )}
    >
      {children}
    </div>
  );
}

function Chip({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className='inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[12px] font-medium text-(--v2-text-primary) shadow-[0_1px_2px_rgb(0_0_0/0.05)] ring-1 ring-black/[0.08] sm:px-3 sm:py-1.5 sm:text-[13px]'>
      {icon}
      {children}
    </li>
  );
}

function SkippedChip({ children }: { children: ReactNode }) {
  return (
    <li className='inline-flex items-center gap-1.5 rounded-full border border-dashed border-black/15 px-2.5 py-1 text-[12px] font-medium text-(--v2-text-muted) sm:px-3 sm:py-1.5 sm:text-[13px]'>
      <Xmark className='size-3.5' strokeWidth={2} />
      <span className='line-through decoration-steel-700/60'>{children}</span>
    </li>
  );
}

function ReadIcon() {
  return <Check className='size-3.5 text-azure-595' strokeWidth={2.5} />;
}

export function CitationRulesMockup() {
  return (
    <Stage className='items-center justify-center px-4 py-5 sm:px-5 sm:py-6'>
      <ul className='flex max-w-sm flex-wrap justify-center gap-2'>
        <Chip icon={<ReadIcon />}>{m.read_along_abstract()}</Chip>
        <SkippedChip>[7, 12]</SkippedChip>
        <Chip icon={<ReadIcon />}>{m.funnel_demo_sec_methods()}</Chip>
        <SkippedChip>{m.homepage_advantages_footnotes()}</SkippedChip>
        <Chip icon={<ReadIcon />}>{m.funnel_demo_sec_results()}</Chip>
        <SkippedChip>{m.why_listening_chip_references()}</SkippedChip>
      </ul>
    </Stage>
  );
}

export function PronunciationMockup() {
  return (
    <Stage className='items-center justify-center px-4 pt-5 pb-14 sm:px-5 sm:pt-6 lg:py-6'>
      <FloatingCard className='w-full max-w-60 rounded-2xl p-3'>
        <div className='flex items-center gap-2.5'>
          <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-azure-595/10 text-azure-595'>
            <SoundHigh className='size-4' />
          </span>
          <span className='truncate text-[13px] font-semibold'>
            heteroscedasticity
          </span>
        </div>
        <Waveform className='mt-3 ml-1 block h-6 w-[118px] text-azure-595/70' />
      </FloatingCard>
      <p className='absolute right-3 bottom-3 inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-(--v2-text-primary) ring-1 ring-black/[0.08] lg:top-3 lg:bottom-auto'>
        <ChatBubbleWarning className='size-3 text-azure-700' />
        {m.why_listening_report_word()}
      </p>
    </Stage>
  );
}

function LibraryRow({ title, meta }: { title: string; meta: string }) {
  return (
    <li className='flex items-center gap-2.5 px-3 py-2'>
      <span className='flex size-7 shrink-0 items-center justify-center rounded-lg bg-steel-100 text-steel-700'>
        <Page className='size-3.5' />
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-[13px] font-semibold'>
          {title}
        </span>
        <span className='block truncate text-[11px] text-steel-700'>
          {meta}
        </span>
      </span>
    </li>
  );
}

export function ZoteroLibraryMockup() {
  return (
    <Stage className='items-end justify-center rounded-none px-5 pt-5 ring-0'>
      <FloatingCard className='w-full max-w-72 rounded-t-2xl'>
        <p className='flex items-center justify-between border-b border-steel-120 px-3 py-2 text-xs font-semibold'>
          {m.why_listening_zotero_library()}
          <span className='font-medium text-steel-700'>
            {m.why_listening_papers_count({ count: 3 })}
          </span>
        </p>
        <ul className='divide-y divide-steel-120'>
          <LibraryRow
            meta={m.why_listening_zotero_thesis_meta()}
            title={m.why_listening_zotero_thesis_title()}
          />
          <LibraryRow
            meta={m.why_listening_zotero_reading_group_meta()}
            title={m.why_listening_zotero_reading_group_title()}
          />
        </ul>
      </FloatingCard>
    </Stage>
  );
}

function InboxCard({
  from,
  subject,
  className,
}: {
  from: string;
  subject: string;
  className: string;
}) {
  return (
    <FloatingCard
      className={cn(
        'flex w-56 items-center gap-2.5 rounded-2xl px-3 py-2.5',
        className,
      )}
    >
      <span className='flex size-7 shrink-0 items-center justify-center rounded-full bg-azure-595/10 text-azure-595'>
        <MailIn className='size-3.5' />
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-[11px] text-steel-700'>
          {from}
        </span>
        <span className='block truncate text-[13px] font-semibold'>
          {subject}
        </span>
      </span>
      <ForwardMessage className='size-4 shrink-0 text-steel-700' />
    </FloatingCard>
  );
}

export function NewsletterInboxMockup() {
  return (
    <Stage className='items-center justify-center px-4 py-5 sm:px-5 sm:py-6'>
      <div className='relative h-28 w-56'>
        <InboxCard
          className='absolute top-0 left-0 -translate-x-3 -rotate-1'
          from={m.why_listening_newsletter_policy_from()}
          subject={m.why_listening_newsletter_policy_subject()}
        />
        <InboxCard
          className='absolute bottom-0 left-0 translate-x-3 rotate-1'
          from={m.why_listening_newsletter_science_from()}
          subject={m.why_listening_newsletter_science_subject()}
        />
      </div>
    </Stage>
  );
}

export function QuotaMockup() {
  return (
    <Stage className='items-center justify-center px-4 py-5 sm:px-5 sm:py-6'>
      <FloatingCard className='w-full max-w-60 space-y-3 rounded-2xl p-3.5'>
        <div>
          <p className='flex items-center justify-between text-[11px] font-semibold'>
            <span className='text-steel-700'>
              {m.why_listening_word_quota()}
            </span>
            <span className='text-gold-625'>92%</span>
          </p>
          <div className='mt-1.5 h-1.5 overflow-hidden rounded-full bg-steel-100'>
            <div className='h-full w-[92%] rounded-full bg-gold-625' />
          </div>
        </div>
        <p className='flex items-center gap-2 border-t border-steel-120 pt-3 text-[13px] font-semibold'>
          <span className='flex size-7 items-center justify-center rounded-full bg-azure-595/10 text-azure-595'>
            <Infinite className='size-4' />
          </span>
          {m.why_listening_unlimited()}
        </p>
      </FloatingCard>
    </Stage>
  );
}

export function DevicesMockup() {
  return (
    <Stage className='items-center justify-center px-4 py-5 sm:px-5 sm:py-6'>
      <div className='flex w-full flex-col items-center gap-3'>
        <ul className='flex max-w-md flex-wrap justify-center gap-2'>
          <Chip icon={<Internet className='size-4' />}>
            {m.why_listening_device_browser()}
          </Chip>
          <Chip icon={<SmartphoneDevice className='size-4' />}>iPhone</Chip>
          <Chip icon={<SmartphoneDevice className='size-4' />}>Android</Chip>
          <Chip icon={<AppleMac className='size-4' />}>Mac</Chip>
          <Chip icon={<Windows className='size-4' />}>Windows</Chip>
          <Chip icon={<Puzzle className='size-4' />}>
            {m.funnel_device_chrome()}
          </Chip>
          <Chip icon={<Car className='size-4' />}>
            {m.why_listening_device_car()}
          </Chip>
          <Chip icon={<Wristwatch className='size-4' />}>
            {m.why_listening_device_watch()}
          </Chip>
        </ul>
        <p className='inline-flex items-center gap-1.5 rounded-full bg-(--v2-text-primary) px-3.5 py-1.5 text-xs font-medium text-white'>
          <Check className='size-3.5' strokeWidth={2.5} />
          {m.why_listening_one_subscription()}
        </p>
      </div>
    </Stage>
  );
}
